import { FontSizes, FontWeights } from '@fluentui/react';
import { makeStyles, mergeClasses } from '@fluentui/react-components';
import * as React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { DismissableContentName, QuickActionName } from '../../constants/app';
import { HomeRenderMilestone } from '../../constants/telemetry';
import { getLinkForOS } from '../../constants/windows-store-links';
import { useActionCreator } from '../../hooks/action-creator';
import {
    useConfigureConnectionDialogContext,
    useConfirmationDialogContext,
    useConnectViaAppDialogContext,
    useConnectWithVSCodeDialogContext,
    useDelayShutdownDialogContext,
    useKeepDevBoxDialogContext,
    useRepairDialogContext,
    useRestoreSnapshotDialogContext,
    useTakeSnapshotDialogContext,
} from '../../hooks/context/dialogs';
import { useProjectFilterContext } from '../../hooks/context/filters';
import {
    useCustomizationDetailsPanelContext,
    useDevBoxDetailsPanelContext,
    useDevBoxSupportPanelContext,
    useErrorDetailsPanelContext,
} from '../../hooks/context/panels';
import { getLocalizationConfiguration } from '../../language/languages';
import { DevBoxActionName } from '../../models/dev-box-action';
import { enableDevBoxTunnel } from '../../redux/actions/add-ons/add-ons-action-creators';
import {
    dismissContent as dismissContentActionCreator,
    dismissQuickAction,
    setDisableRemoteDesktopStatus as setDisableRemoteDesktopStatusActionCreator,
} from '../../redux/actions/application/application-action-creators';
import {
    delayAllDevBoxActions,
    skipAllDevBoxActions,
} from '../../redux/actions/dev-box-action/dev-box-action-action-creators';
import {
    deleteDevBox,
    hibernateDevBox,
    repairDevBox,
    restartDevBox,
    restoreSnapshot,
    resumeDevBox,
    shutdownDevBox,
    startDevBox,
    takeSnapshotDevBox,
} from '../../redux/actions/dev-box/dev-box-action-creators';
import { getRemoteConnection } from '../../redux/actions/remote-connection/remote-connection-action-creators';
import { getDisableRemoteDesktopStatus as getDisableRemoteDesktopStatusFromStore } from '../../redux/selector/application-selectors';
import { getDismissedQuickActionsForHome } from '../../redux/selector/sub-applications/home-selectors';
import { getUserSettingsSelector } from '../../redux/selector/user-settings-selector';
import { CssSelector } from '../../themes/constants/css-selectors';
import { useHorizontalStackStyles, useStackStyles } from '../../themes/styles/flexbox-styles';
import { ReturnVoid } from '../../types/return-void';
import { SerializableSet } from '../../types/serializable-set';
import { RemoteAppOption } from '../../types/user-settings';
import { getOperatingSystemFamily, openInNewWindow } from '../../utilities/browser';
import { ConfirmationDialogProperties } from '../common/confirmation-dialog/contexts';
import { CustomizationDetailsPanelContextProperties } from '../customization-details-panel/contexts';
import { ErrorDetailsPanelContextProperties } from '../error-details-panel/contexts';
import { ConnectWithVSCodeDialogContextProperties } from '../quick-action-dialogs/connect-with-vscode-dialog/connect-with-vscode-dialog-context';
import { OpenConnectViaAppDialogProperties } from '../user-settings/connect-via-app-dialog/contexts';
import ConfigureConnectionCard from './configure-connection-dialog/configure-connection-quick-action-card';
import { DelayShutdownDialogProperties } from './delay-shutdown-dialog/contexts';
import DevBoxCard from './dev-box-card/dev-box-card';
import { DevBoxDetailsPanelContextProperties } from './dev-box-details-panel/context';
import { DevBoxSupportPanelContextProperties } from './dev-box-support-panel/context';
import { KeepDevBoxDialogProperties } from './keep-devbox-dialog/context';
import { McpPromotionCard } from './mcp-promotion-dialog/mcp-promotion-quick-action-card';
import { DevBoxViewModel } from './models';
import { RepairDialogProperties } from './repair-dialog/context';
import { RestoreSnapshotDialogProperties } from './restore-snapshot-dialog/contexts';
import {
    getDevBoxViewModels,
    getDevBoxViewModelsInProject,
    getShowConfigureRemoteDesktopQuickAction,
    getShowMcpPromotionQuickAction,
    getShowQuickActionsSection,
} from './selectors';
import { TakeSnapshotDialogProperties } from './take-snapshot/context';

interface DevBoxesComponentProps {
    devBoxes: DevBoxViewModel[];
    selectedProjectFilter: string | undefined;
    dismissedQuickActions: SerializableSet<QuickActionName>;
    locale: string;
    onDeleteDevBoxSubmitted: ReturnVoid<typeof deleteDevBox>;
    onDismissQuickAction: ReturnVoid<typeof dismissQuickAction>;
    onGetRemoteConnectionForDevBoxSubmitted: ReturnVoid<typeof getRemoteConnection>;
    onOpenErrorPanel: (props: ErrorDetailsPanelContextProperties) => void;
    onOpenDevBoxDetailsPanel: (props: DevBoxDetailsPanelContextProperties) => void;
    onOpenDevBoxSupportPanel: (props: DevBoxSupportPanelContextProperties) => void;
    onStartDevBoxSubmitted: ReturnVoid<typeof startDevBox>;
    onEnableDevBoxTunnelSubmitted: ReturnVoid<typeof enableDevBoxTunnel>;
    onDelayAllSubmitted: ReturnVoid<typeof delayAllDevBoxActions>;
    onSkipAllSubmitted: ReturnVoid<typeof skipAllDevBoxActions>;
    onOpenConfigureConnectionDialog: () => void;
    onOpenDelayShutdownDialog: (properties: DelayShutdownDialogProperties) => void;
    onOpenRestoreSnapshotDialog: (properties: RestoreSnapshotDialogProperties) => void;
    onOpenConnectViaAppDialog: (properties: OpenConnectViaAppDialogProperties) => void;
    onOpenConnectWithVSCodeDialog: (properties: ConnectWithVSCodeDialogContextProperties) => void;
    onOpenConfirmationDialog: (properties: ConfirmationDialogProperties) => void;
    onHibernateDevBoxSubmitted: ReturnVoid<typeof hibernateDevBox>;
    onShutdownDevBoxSubmitted: ReturnVoid<typeof shutdownDevBox>;
    onRestartDevBoxSubmitted: ReturnVoid<typeof restartDevBox>;
    onResumeDevBoxSubmitted: ReturnVoid<typeof resumeDevBox>;
    onRepairDevBoxSubmitted: ReturnVoid<typeof repairDevBox>;
    onTakeSnapshotDevBoxSubmitted: ReturnVoid<typeof takeSnapshotDevBox>;
    onRestoreSnapshotSubmitted: ReturnVoid<typeof restoreSnapshot>;
    onOpenRepairDialog: (properties: RepairDialogProperties) => void;
    onOpenTakeSnapshotDialog: (properties: TakeSnapshotDialogProperties) => void;
    onOpenCustomizationPanel: (props: CustomizationDetailsPanelContextProperties) => void;
    onOpenKeepDevBoxDialog: (properties: KeepDevBoxDialogProperties) => void;
}

const messages = defineMessages({
    yourDevBoxMessage: {
        id: 'DevBoxes_YourDevBoxMessage_Text',
        defaultMessage: 'Your dev box',
        description: 'This text is informing the user that their dev box is listed on this page',
    },
    yourDevBoxesPluralMessage: {
        id: 'DevBoxes_YourDevBoxPluralMessage_Text',
        defaultMessage: 'Your dev boxes',
        description: 'This text is informing the user that their dev boxes are listed on this page',
    },
});

/**
 * Styles
 */

const useGridStyles = makeStyles({
    root: {
        width: '100%',
        flexWrap: 'wrap',
    },
});

const useGridItemStyles = makeStyles({
    root: {
        marginBottom: '24px',
        marginRight: '12px',

        [CssSelector.ScreenSizeSmallAndBelow]: {
            width: '100%',
        },

        [CssSelector.ScreenSizeXXLarge]: {
            marginRight: '24px',
        },
    },
});

const useSectionContentStyles = makeStyles({
    root: {
        width: 'auto',

        [CssSelector.ScreenSizeSmallAndBelow]: {
            width: '100%',
        },
    },
});

const useSectionHeaderStyles = makeStyles({
    h2: {
        fontSize: FontSizes.size16,
        fontWeight: FontWeights.semibold,
        margin: 0,
    },
});

const useContainerStyles = makeStyles({
    root: {
        gap: '26px',
    },
});

const useSectionStyles = makeStyles({
    root: {
        gap: '11px',
        alignItems: 'start',
    },
});

/**
 * End Styles
 */

const onOpenWindowsAppLink = () => {
    const operatingSystem = getOperatingSystemFamily();
    const link = getLinkForOS(operatingSystem);
    openInNewWindow(link);
};

export const DevBoxesComponent: React.FC<DevBoxesComponentProps> = React.memo((props: DevBoxesComponentProps) => {
    const {
        devBoxes,
        selectedProjectFilter,
        dismissedQuickActions,
        locale,
        onDeleteDevBoxSubmitted,
        onDismissQuickAction,
        onGetRemoteConnectionForDevBoxSubmitted,
        onOpenErrorPanel,
        onOpenDevBoxDetailsPanel,
        onStartDevBoxSubmitted,
        onEnableDevBoxTunnelSubmitted,
        onDelayAllSubmitted,
        onSkipAllSubmitted,
        onOpenConfigureConnectionDialog,
        onOpenDelayShutdownDialog,
        onOpenRestoreSnapshotDialog,
        onOpenConfirmationDialog,
        onHibernateDevBoxSubmitted,
        onShutdownDevBoxSubmitted,
        onRestartDevBoxSubmitted,
        onResumeDevBoxSubmitted,
        onRepairDevBoxSubmitted,
        onTakeSnapshotDevBoxSubmitted,
        onRestoreSnapshotSubmitted,
        onOpenRepairDialog,
        onOpenTakeSnapshotDialog,
        onOpenCustomizationPanel,
        onOpenDevBoxSupportPanel,
        onOpenConnectViaAppDialog,
        onOpenConnectWithVSCodeDialog,
        onOpenKeepDevBoxDialog,
    } = props;

    // Style hooks
    const gridStyles = useGridStyles();
    const gridItemStyles = useGridItemStyles();
    const sectionContentStyles = useSectionContentStyles();
    const sectionHeaderStyles = useSectionHeaderStyles();
    const stackStyles = useStackStyles();
    const containerStyles = useContainerStyles();
    const sectionStyles = useSectionStyles();
    const horizontalStackStyles = useHorizontalStackStyles();

    // Application state hooks
    const getDisableRemoteDesktopStatus = useSelector(getDisableRemoteDesktopStatusFromStore);
    const userSettings = useSelector(getUserSettingsSelector);

    // Action Creators
    const dismissContent = useActionCreator(dismissContentActionCreator);
    const setDisableRemoteDesktopStatus = useActionCreator(setDisableRemoteDesktopStatusActionCreator);

    // Callback hooks
    const onConfigureRemoteDesktopActionDismissed = React.useCallback(() => {
        onDismissQuickAction({
            quickAction: QuickActionName.ConfigureRemoteDesktopPostAvdDeprecation,
        });
    }, [onDismissQuickAction]);

    const onMcpPromotionActionDismissed = React.useCallback(() => {
        onDismissQuickAction({
            quickAction: QuickActionName.McpPromotion,
        });
    }, [onDismissQuickAction]);

    const onDelayShutdownSubmitted = React.useCallback(
        (id: string, delayUntil: Date) => {
            onDelayAllSubmitted({ delayUntil, id });
        },
        [onDelayAllSubmitted]
    );

    const onSkipScheduledDeleteSubmitted = React.useCallback(
        (id: string) => {
            onSkipAllSubmitted({ id, within24Hours: false, actionName: DevBoxActionName.DeleteByAdmin });
        },
        [onSkipAllSubmitted]
    );

    const onConnectViaAppTeachBubbleDismiss = React.useCallback(() => {
        // Once the remote desktop deprecation is enabled, we use a different dismissable content name
        // We have logic for people that have dismissed the bubble before the deprecation to ensure they
        // don't inappropriately see the bubble again.
        dismissContent({ content: DismissableContentName.ConnectViaAppRemoteDesktopDeprecatedTeachableBubble });
        setDisableRemoteDesktopStatus({
            siteLoadSinceDisableRemoteDesktop: new Date(),
        });
    }, [dismissContent]);

    const onConnectViaAppTeachingPopoverForWelcomeTourDismiss = React.useCallback(() => {
        dismissContent({ content: DismissableContentName.ConnectViaAppTeachingPopoverForWelcomeTour });
    }, [dismissContent]);

    const onConnectViaAppTeachingPopoverForWelcomeTourSkipped = React.useCallback(() => {
        dismissContent({ content: DismissableContentName.ConnectViaAppTeachingPopoverForWelcomeTourSkipped });
    }, [dismissContent]);

    const onDevboxSecondaryActionsTeachingPopoverForWelcomeTourDismiss = React.useCallback(() => {
        dismissContent({ content: DismissableContentName.DevboxSecondaryActionsTeachingPopoverForWelcomeTour });
    }, [dismissContent]);

    const onConnectWithVSCodeTeachingPopoverDismiss = React.useCallback(() => {
        dismissContent({ content: DismissableContentName.ConnectWithVSCodeTeachingPopoverDismissed });
    }, [dismissContent]);

    const onDeleteSubmitted = React.useCallback(
        (id: string) => {
            onDeleteDevBoxSubmitted({ id });
        },
        [onDeleteDevBoxSubmitted]
    );

    const onGetRemoteConnectionSubmitted = React.useCallback(
        (id: string) => {
            onGetRemoteConnectionForDevBoxSubmitted({ id });
        },
        [onGetRemoteConnectionForDevBoxSubmitted]
    );

    const onHibernateSubmitted = React.useCallback(
        (id: string) => {
            onHibernateDevBoxSubmitted({ id });
        },
        [onHibernateDevBoxSubmitted]
    );

    const onRepairSubmitted = React.useCallback(
        (id: string) => {
            onRepairDevBoxSubmitted({ id });
        },
        [onRepairDevBoxSubmitted]
    );

    const onTakeSnapshotSubmitted = React.useCallback(
        (id: string) => {
            onTakeSnapshotDevBoxSubmitted({ id });
        },
        [onTakeSnapshotDevBoxSubmitted]
    );

    const onRestartSubmitted = React.useCallback(
        (id: string) => {
            onRestartDevBoxSubmitted({ id });
        },
        [onRestartDevBoxSubmitted]
    );

    const onResumeSubmitted = React.useCallback(
        (id: string) => {
            onResumeDevBoxSubmitted({ id });
        },
        [onResumeDevBoxSubmitted]
    );

    const onRestoreSubmitted = React.useCallback(
        (id: string, snapshotId: string) => {
            onRestoreSnapshotSubmitted({ id, snapshotId });
        },
        [onRestoreSnapshotSubmitted]
    );

    const onShutdownSubmitted = React.useCallback(
        (id: string) => {
            onShutdownDevBoxSubmitted({ id });
        },
        [onShutdownDevBoxSubmitted]
    );

    const onSkipShutdownSubmitted = React.useCallback(
        (id: string) => {
            onSkipAllSubmitted({ id });
        },
        [onSkipAllSubmitted]
    );

    const onStartSubmitted = React.useCallback(
        (id: string) => {
            onStartDevBoxSubmitted({ id });
        },
        [onStartDevBoxSubmitted]
    );

    const onEnableWindowsTunnelSubmitted = React.useCallback(
        (id: string) => {
            onEnableDevBoxTunnelSubmitted({ id });
        },
        [onEnableDevBoxTunnelSubmitted]
    );

    // Memoized data
    const hasAnyDevBoxBeenConnectedToSinceAVDDeprecation = React.useMemo(
        () =>
            // For the proper AVD deprecation experience, we want to know if
            // 1. The user has visited the site since the deprecation
            // 2. Before deprecation, the user selected to use the AVD app to connect
            // 3. It's been more than an hour since the user saw the initial teaching bubble
            // 4. The user has not connected to any dev boxes since the deprecation
            !!getDisableRemoteDesktopStatus.siteLoadSinceDisableRemoteDesktop &&
            userSettings.preferredRemoteApp === RemoteAppOption.useAvd &&
            Date.now() >
                new Date(getDisableRemoteDesktopStatus.siteLoadSinceDisableRemoteDesktop).getTime() + 60 * 60 * 1000 &&
            !devBoxes.some((devBox) => {
                return (
                    !!getDisableRemoteDesktopStatus.siteLoadSinceDisableRemoteDesktop &&
                    !!devBox.resource.lastConnectedTime &&
                    devBox.resource.lastConnectedTime >
                        new Date(getDisableRemoteDesktopStatus.siteLoadSinceDisableRemoteDesktop)
                );
            }),
        [devBoxes, getDisableRemoteDesktopStatus, userSettings]
    );

    const showConfigureRemoteDesktopQuickAction = React.useMemo(() => {
        return getShowConfigureRemoteDesktopQuickAction(
            dismissedQuickActions,
            hasAnyDevBoxBeenConnectedToSinceAVDDeprecation
        );
    }, [dismissedQuickActions, hasAnyDevBoxBeenConnectedToSinceAVDDeprecation]);

    const showMcpPromotionQuickAction = React.useMemo(() => {
        return getShowMcpPromotionQuickAction(dismissedQuickActions);
    }, [dismissedQuickActions]);

    const showQuickActionsSection = React.useMemo(
        () => getShowQuickActionsSection(dismissedQuickActions, hasAnyDevBoxBeenConnectedToSinceAVDDeprecation),
        [dismissedQuickActions, hasAnyDevBoxBeenConnectedToSinceAVDDeprecation]
    );

    const filteredDevBoxes = React.useMemo(
        () => (selectedProjectFilter ? getDevBoxViewModelsInProject(devBoxes, selectedProjectFilter) : devBoxes),
        [devBoxes, selectedProjectFilter]
    );

    const yourDevBoxMessage = React.useMemo(
        () =>
            filteredDevBoxes.length === 0
                ? undefined
                : filteredDevBoxes.length > 1
                ? messages.yourDevBoxesPluralMessage
                : messages.yourDevBoxMessage,
        [filteredDevBoxes, messages]
    );

    return (
        <>
            <div className={mergeClasses(stackStyles.root, containerStyles.root)}>
                {showQuickActionsSection && (
                    <div className={stackStyles.item}>
                        <div className={mergeClasses(stackStyles.root, sectionStyles.root)}>
                            <h2 className={mergeClasses(stackStyles.item, sectionHeaderStyles.h2)}>
                                <FormattedMessage
                                    id="DevBoxes_QuickActionsHeader_Text"
                                    defaultMessage="Quick actions"
                                    description="Header text for the 'Quick Actions' section"
                                />
                            </h2>

                            <div className={mergeClasses(stackStyles.item, sectionContentStyles.root)}>
                                <div className={mergeClasses(horizontalStackStyles.root, gridStyles.root)}>
                                    {showConfigureRemoteDesktopQuickAction && (
                                        <div className={mergeClasses(horizontalStackStyles.item, gridItemStyles.root)}>
                                            <ConfigureConnectionCard
                                                onActionInvoked={onOpenConfigureConnectionDialog}
                                                onDismiss={onConfigureRemoteDesktopActionDismissed}
                                                onWindowsAppLinkInvoked={onOpenWindowsAppLink}
                                            />
                                        </div>
                                    )}
                                    {showMcpPromotionQuickAction && (
                                        <div className={mergeClasses(horizontalStackStyles.item, gridItemStyles.root)}>
                                            <McpPromotionCard onDismiss={onMcpPromotionActionDismissed} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className={stackStyles.item}>
                    <div className={mergeClasses(stackStyles.root, sectionStyles.root)}>
                        <div className={stackStyles.item}>
                            <h2 className={sectionHeaderStyles.h2}>
                                {yourDevBoxMessage && <FormattedMessage {...yourDevBoxMessage} />}
                            </h2>
                        </div>

                        <div className={mergeClasses(stackStyles.item, sectionContentStyles.root)}>
                            <div className={mergeClasses(horizontalStackStyles.root, gridStyles.root)}>
                                {filteredDevBoxes.map((devBox: DevBoxViewModel, index: number) => (
                                    <div
                                        id="DevBoxCard"
                                        data-home-render-milestone={HomeRenderMilestone.DevBoxCard}
                                        className={mergeClasses(horizontalStackStyles.item, gridItemStyles.root)}
                                        key={devBox.key}
                                    >
                                        <DevBoxCard
                                            devBox={devBox}
                                            locale={locale}
                                            onDeleteSubmitted={onDeleteSubmitted}
                                            onGetRemoteConnectionSubmitted={onGetRemoteConnectionSubmitted}
                                            onOpenErrorPanel={onOpenErrorPanel}
                                            onOpenDevBoxDetailsPanel={onOpenDevBoxDetailsPanel}
                                            onStartSubmitted={onStartSubmitted}
                                            onEnableWindowsTunnelSubmitted={onEnableWindowsTunnelSubmitted}
                                            onDelayShutdownSubmitted={onDelayShutdownSubmitted}
                                            onSkipShutdownSubmitted={onSkipShutdownSubmitted}
                                            onOpenDelayShutdownDialog={onOpenDelayShutdownDialog}
                                            onOpenRestoreSnapshotDialog={onOpenRestoreSnapshotDialog}
                                            onRestoreSubmitted={onRestoreSubmitted}
                                            onTakeSnapshotSubmitted={onTakeSnapshotSubmitted}
                                            onOpenConnectViaAppDialog={onOpenConnectViaAppDialog}
                                            onOpenConnectWithVSCodeDialog={onOpenConnectWithVSCodeDialog}
                                            onOpenConfirmationDialog={onOpenConfirmationDialog}
                                            onHibernateSubmitted={onHibernateSubmitted}
                                            onShutdownSubmitted={onShutdownSubmitted}
                                            onResumeSubmitted={onResumeSubmitted}
                                            onRestartSubmitted={onRestartSubmitted}
                                            onRepairSubmitted={onRepairSubmitted}
                                            onOpenRepairDialog={onOpenRepairDialog}
                                            onOpenTakeSnapshotDialog={onOpenTakeSnapshotDialog}
                                            onOpenCustomizationPanel={onOpenCustomizationPanel}
                                            onOpenDevBoxSupportPanel={onOpenDevBoxSupportPanel}
                                            onConnectViaAppTeachBubbleDismiss={onConnectViaAppTeachBubbleDismiss}
                                            onConnectViaAppTeachingPopoverForWelcomeTourDismiss={
                                                onConnectViaAppTeachingPopoverForWelcomeTourDismiss
                                            }
                                            onConnectViaAppTeachingPopoverForWelcomeTourSkipped={
                                                onConnectViaAppTeachingPopoverForWelcomeTourSkipped
                                            }
                                            onDevboxSecondaryActionsTeachingPopoverForWelcomeTourDismiss={
                                                onDevboxSecondaryActionsTeachingPopoverForWelcomeTourDismiss
                                            }
                                            shouldShowConnectViaAppTeachBubble={index === 0}
                                            shouldShowConnectViaAppTeachingPopoverForWelcomeTour={index === 0}
                                            shouldShowDevboxSecondaryActionsTeachingPopoverForWelcomeTour={index === 0}
                                            onSkipScheduledDeleteSubmitted={onSkipScheduledDeleteSubmitted}
                                            onOpenKeepDevBoxDialog={onOpenKeepDevBoxDialog}
                                            onConnectWithVSCodeTeachingPopoverDismiss={
                                                onConnectWithVSCodeTeachingPopoverDismiss
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
});

export const DevBoxes: React.FC = () => {
    const { locale } = getLocalizationConfiguration();
    const { value } = useProjectFilterContext();

    // Application state hooks
    const devBoxes = useSelector(getDevBoxViewModels);
    const dismissedQuickActions = useSelector(getDismissedQuickActionsForHome);

    // Action hooks
    const onDeleteDevBoxSubmitted = useActionCreator(deleteDevBox);
    const onDismissQuickAction = useActionCreator(dismissQuickAction);
    const onGetRemoteConnectionForDevBoxSubmitted = useActionCreator(getRemoteConnection);
    const onStartDevBoxSubmitted = useActionCreator(startDevBox);
    const onEnableDevBoxTunnelSubmitted = useActionCreator(enableDevBoxTunnel);
    const onDelayAllSubmitted = useActionCreator(delayAllDevBoxActions);
    const onSkipAllSubmitted = useActionCreator(skipAllDevBoxActions);
    const onHibernateDevBoxSubmitted = useActionCreator(hibernateDevBox);
    const onShutdownDevBoxSubmitted = useActionCreator(shutdownDevBox);
    const onRestartDevBoxSubmitted = useActionCreator(restartDevBox);
    const onResumeDevBoxSubmitted = useActionCreator(resumeDevBox);
    const onRepairDevBoxSubmitted = useActionCreator(repairDevBox);
    const onTakeSnapshotDevBoxSubmitted = useActionCreator(takeSnapshotDevBox);
    const onRestoreSnapshotSubmitted = useActionCreator(restoreSnapshot);

    // Context hooks
    const { openSurface: onOpenErrorPanel } = useErrorDetailsPanelContext();
    const { openSurface: onOpenDevBoxDetailsPanel } = useDevBoxDetailsPanelContext();
    const { openSurface: onOpenDevBoxSupportPanel } = useDevBoxSupportPanelContext();
    const { openSurface: onOpenConfigureConnectionDialogContext } = useConfigureConnectionDialogContext();
    const { openSurface: onOpenDelayShutdownDialog } = useDelayShutdownDialogContext();
    const { openSurface: onOpenRestoreSnapshotDialog } = useRestoreSnapshotDialogContext();
    const { openSurface: onOpenConfirmationDialog } = useConfirmationDialogContext();
    const { openSurface: onOpenRepairDialog } = useRepairDialogContext();
    const { openSurface: onOpenTakeSnapshotDialog } = useTakeSnapshotDialogContext();
    const { openSurface: onOpenCustomizationPanel } = useCustomizationDetailsPanelContext();
    const { openSurface: onOpenConnectViaAppDialog } = useConnectViaAppDialogContext();
    const { openSurface: onOpenConnectWithVSCodeDialog } = useConnectWithVSCodeDialogContext();
    const { openSurface: onOpenKeepDevBoxDialog } = useKeepDevBoxDialogContext();

    return (
        <DevBoxesComponent
            devBoxes={devBoxes}
            selectedProjectFilter={value}
            dismissedQuickActions={dismissedQuickActions}
            locale={locale}
            onDeleteDevBoxSubmitted={onDeleteDevBoxSubmitted}
            onDismissQuickAction={onDismissQuickAction}
            onGetRemoteConnectionForDevBoxSubmitted={onGetRemoteConnectionForDevBoxSubmitted}
            onOpenErrorPanel={onOpenErrorPanel}
            onOpenDevBoxDetailsPanel={onOpenDevBoxDetailsPanel}
            onStartDevBoxSubmitted={onStartDevBoxSubmitted}
            onEnableDevBoxTunnelSubmitted={onEnableDevBoxTunnelSubmitted}
            onDelayAllSubmitted={onDelayAllSubmitted}
            onSkipAllSubmitted={onSkipAllSubmitted}
            onOpenConfigureConnectionDialog={onOpenConfigureConnectionDialogContext}
            onOpenConnectViaAppDialog={onOpenConnectViaAppDialog}
            onOpenConnectWithVSCodeDialog={onOpenConnectWithVSCodeDialog}
            onOpenDelayShutdownDialog={onOpenDelayShutdownDialog}
            onOpenRestoreSnapshotDialog={onOpenRestoreSnapshotDialog}
            onOpenConfirmationDialog={onOpenConfirmationDialog}
            onHibernateDevBoxSubmitted={onHibernateDevBoxSubmitted}
            onShutdownDevBoxSubmitted={onShutdownDevBoxSubmitted}
            onRestartDevBoxSubmitted={onRestartDevBoxSubmitted}
            onResumeDevBoxSubmitted={onResumeDevBoxSubmitted}
            onRepairDevBoxSubmitted={onRepairDevBoxSubmitted}
            onTakeSnapshotDevBoxSubmitted={onTakeSnapshotDevBoxSubmitted}
            onRestoreSnapshotSubmitted={onRestoreSnapshotSubmitted}
            onOpenRepairDialog={onOpenRepairDialog}
            onOpenTakeSnapshotDialog={onOpenTakeSnapshotDialog}
            onOpenCustomizationPanel={onOpenCustomizationPanel}
            onOpenDevBoxSupportPanel={onOpenDevBoxSupportPanel}
            onOpenKeepDevBoxDialog={onOpenKeepDevBoxDialog}
        />
    );
};

export default DevBoxes;
